import * as THREE from 'three'
import { ProtoBase } from '../ProtoBase'
import { SITES } from '../kanjinData'

/**
 * ⑬ 六十六寄進地・光のトンネル — ジェットコースター演出。
 * 66枚の「寄進札」（12寄進地の実写写真＋木札）が螺旋状に並ぶ光のトンネルを、
 * 畳に乗って急降下・急旋回しながら駆け抜け、終端の一畳敷の光へ飛び込む。
 * 勧進帳の66人の縁を「距離」として体感させる。
 */
export class Proto13 extends ProtoBase {
  readonly title = '13. 六十六寄進地・光のトンネル'
  readonly desc = '光のチューブの中を、66枚の大判写真と宙に浮かぶ建造物名（立体文字）をくぐり抜けて畳で飛ぶ（緩カーブの快適コース）'

  private curve!: THREE.CatmullRomCurve3
  private u = 0
  private plates: { t: number; name: string; hit: boolean }[] = []
  private rings: THREE.Mesh[] = []
  private names: { group: THREE.Group; baseY: number }[] = []
  private tubeTex!: THREE.Texture
  private endGlow!: THREE.Sprite
  private endT = 0
  private tmp = new THREE.Vector3()
  private tmp2 = new THREE.Vector3()

  protected start(): void {
    this.ctx.setWorld('void')
    this.ctx.view.driftEnabled = false // レールカメラ演出
    this.ctx.scene.background = new THREE.Color(0x05070d)
    this.ctx.scene.fog = new THREE.Fog(0x05070d, 30, 170)
    this.ctx.deck.setVisible(true)
    if (this.ctx.narrator.loaded) this.ctx.narrator.root.visible = true
    this.ctx.setCaption('六十六の縁の中を——飛ぶぞ、摑まっておれ！')

    // ── コース: ゆるやかな大回りだけの快適コース（酔い対策: 急カーブ・急落下を排除）──
    const pts: THREE.Vector3[] = []
    for (let i = 0; i <= 14; i++) {
      pts.push(new THREE.Vector3(
        Math.sin(i * 0.9) * 16,
        Math.sin(i * 0.55) * 7 - i * 0.9 + (i === 9 ? -9 : 0), // 1箇所だけ緩い下り坂
        -i * 46,
      ))
    }
    this.curve = new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.45)

    // ── 光のチューブ: 進行方向へ流れる光の帯（内側から見る）──
    this.tubeTex = Proto13.makeTubeTexture()
    const tube = new THREE.Mesh(
      new THREE.TubeGeometry(this.curve, 240, 9.4, 20, false),
      new THREE.MeshBasicMaterial({
        map: this.tubeTex, transparent: true, opacity: 0.30, side: THREE.BackSide,
        blending: THREE.AdditiveBlending, depthWrite: false, color: 0xffe2a8,
      }),
    )
    this.group.add(tube)

    // ── 66枚の寄進札（写真プレート＋木札）を螺旋配置 ──
    const texLoader = new THREE.TextureLoader()
    const photoMats = SITES.map((s) => {
      const m = new THREE.MeshBasicMaterial({ color: 0x666666, side: THREE.DoubleSide })
      texLoader.load(`./assets/sites/${s.no}.jpg`, (t) => {
        t.colorSpace = THREE.SRGBColorSpace
        m.map = t
        m.color.set(0xffffff)
        m.needsUpdate = true
      })
      return m
    })
    const frameMat = new THREE.MeshBasicMaterial({ color: 0x8a6d2f })
    const GOLDEN = 2.399963
    for (let i = 0; i < 66; i++) {
      const t = 0.025 + (i / 66) * 0.93
      const p = this.curve.getPointAt(t)
      const tan = this.curve.getTangentAt(t)
      const n = this.tmp.set(tan.z, 0, -tan.x).normalize().clone() // 水平法線
      const b = new THREE.Vector3().crossVectors(n, tan).normalize()
      const ang = i * GOLDEN
      const off = n.clone().multiplyScalar(Math.cos(ang) * 8.5).addScaledVector(b, Math.sin(ang) * 8.5)

      const site = SITES[i % SITES.length]
      const g = new THREE.Group()
      g.position.copy(p).add(off)
      g.lookAt(g.position.clone().sub(tan)) // 進行してくるライダーへ正対（正面向き）
      const frame = new THREE.Mesh(new THREE.PlaneGeometry(8.2, 6.3), frameMat)
      frame.position.z = -0.02
      g.add(frame)
      g.add(new THREE.Mesh(new THREE.PlaneGeometry(7.6, 5.7), photoMats[i % photoMats.length]))
      const tag = makeKanjinTag(i + 1, site.building)
      tag.position.set(0, -3.9, 0.05)
      g.add(tag)
      this.group.add(g)
      this.plates.push({ t, name: site.building, hit: false })
    }

    // ── 建造物名の3D浮遊ネーム: レーン上空に浮かび、その下をくぐり抜けていく ──
    for (let i = 0; i < SITES.length; i++) {
      const t = 0.06 + (i / SITES.length) * 0.86
      const p = this.curve.getPointAt(t)
      const tan = this.curve.getTangentAt(t)
      const g = makeName3D(SITES[i].building)
      const baseY = p.y + (i % 2 === 0 ? 4.6 : 3.6)
      g.position.set(p.x, baseY, p.z)
      g.lookAt(p.clone().sub(tan)) // 進行してくるライダーへ正対
      this.group.add(g)
      this.names.push({ group: g, baseY })
    }

    // ── 光のリング（通過ゲート）──
    const ringMat = new THREE.MeshBasicMaterial({
      color: 0xffc860, transparent: true, opacity: 0.55, blending: THREE.AdditiveBlending,
      depthWrite: false, side: THREE.DoubleSide,
    })
    for (let i = 0; i < 14; i++) {
      const t = 0.04 + (i / 14) * 0.9
      const ring = new THREE.Mesh(new THREE.TorusGeometry(9.0, 0.09, 8, 48), ringMat.clone())
      const p = this.curve.getPointAt(t)
      ring.position.copy(p)
      ring.lookAt(p.clone().add(this.curve.getTangentAt(t)))
      this.rings.push(ring)
      this.group.add(ring)
    }

    // ── 浮遊する木片（流れの体感）──
    const chipGeo = new THREE.BufferGeometry()
    const cp: number[] = []
    for (let i = 0; i < 500; i++) {
      const t = Math.random()
      const p = this.curve.getPointAt(t)
      cp.push(p.x + (Math.random() - 0.5) * 14, p.y + (Math.random() - 0.5) * 14, p.z + (Math.random() - 0.5) * 14)
    }
    chipGeo.setAttribute('position', new THREE.Float32BufferAttribute(cp, 3))
    this.group.add(new THREE.Points(chipGeo, new THREE.PointsMaterial({
      color: 0xd8b070, size: 0.32, transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending, depthWrite: false,
    })))

    // ── 終端: 一畳敷の光 ──
    const end = this.curve.getPointAt(1)
    this.endGlow = makeWarmGlow()
    this.endGlow.position.copy(end).add(new THREE.Vector3(0, 0, -8))
    this.endGlow.scale.setScalar(26)
    this.group.add(this.endGlow)
    const tatami = new THREE.Mesh(
      new THREE.BoxGeometry(0.91 * 4, 0.2, 1.82 * 4),
      new THREE.MeshBasicMaterial({ color: 0x9fd08a, transparent: true, opacity: 0.9 }),
    )
    tatami.position.copy(end).add(new THREE.Vector3(0, -1.5, -10))
    this.group.add(tatami)

    this.u = 0
    this.endT = 0
  }

  update(dt: number): void {
    this.elapsed += dt

    if (this.endT === 0) {
      // 速度プロファイル: 下り勾配で加速（ジェットコースター感）。全長約45秒
      const tan = this.curve.getTangentAt(Math.min(this.u, 1))
      const boost = THREE.MathUtils.clamp(-tan.y * 3.2, 0, 1.4)
      const startEase = Math.min(1, this.elapsed / 3) // 出だしはゆっくり
      this.u = Math.min(1, this.u + dt * (1 / 52) * (0.55 + boost + 0.45 * startEase))

      const p = this.curve.getPointAt(this.u)
      const a = this.curve.getPointAt(Math.min(1, this.u + 0.008))
      this.ctx.view.setPose(
        this.tmp.copy(p).add(this.tmp2.set(0, 1.5, 0)),
        a.clone().add(new THREE.Vector3(0, 1.2, 0)),
      )

      // 通過演出: 札の前を抜けるたびチャイム、6枚ごとにキャプション
      for (let i = 0; i < this.plates.length; i++) {
        const pl = this.plates[i]
        if (!pl.hit && this.u > pl.t - 0.002) {
          pl.hit = true
          if (i % 6 === 0) {
            this.ctx.sfx.play('chime')
            this.ctx.setCaption(`第${kanjiNum(i + 1)}番 ${pl.name}——`)
          }
        }
      }
      if (this.u >= 1) {
        this.endT = this.elapsed
        this.ctx.sfx.play('drum')
        this.ctx.sfx.play('bell')
        this.ctx.setCaption('六十六の縁を抜けて——八十九の木片、一畳へ')
      }
      // 急降下の風切り
      if (boost > 1.0 && Math.random() < dt * 2) this.ctx.sfx.play('whoosh')
    } else {
      // ゴール: 光が膨らんでループ
      const t = this.elapsed - this.endT
      this.endGlow.scale.setScalar(26 + t * 30)
      if (t > 4.5) {
        this.plates.forEach((pl) => (pl.hit = false))
        this.elapsed = 0
        this.u = 0
        this.endT = 0
        this.endGlow.scale.setScalar(26)
        this.ctx.setCaption('もう一巡——六十六の縁の中を、飛ぶ！')
      }
    }

    // リングの明滅
    this.rings.forEach((r, i) => {
      const m = r.material as THREE.MeshBasicMaterial
      m.opacity = 0.4 + 0.3 * Math.sin(this.elapsed * 3 + i * 0.9)
    })
    // 光のチューブ: 帯が進行方向へ流れる
    this.tubeTex.offset.x -= dt * 0.35
    // 3Dネームの浮遊（ゆっくり上下・微揺れ）
    this.names.forEach((n, i) => {
      n.group.position.y = n.baseY + Math.sin(this.elapsed * 1.1 + i * 1.7) * 0.45
      n.group.rotation.z = Math.sin(this.elapsed * 0.7 + i) * 0.03
    })
  }

  protected onDispose(): void {
    this.ctx.view.driftEnabled = true
    this.ctx.scene.fog = null
    this.ctx.deck.setVisible(this.ctx.view.xrActive)
    this.ctx.narrator.root.visible = this.ctx.view.xrActive
  }

  /** 進行方向へ流れる光の帯テクスチャ（チューブ内壁用） */
  private static makeTubeTexture(): THREE.Texture {
    const c = document.createElement('canvas')
    c.width = 512
    c.height = 64
    const g = c.getContext('2d')!
    g.clearRect(0, 0, 512, 64)
    for (let i = 0; i < 22; i++) {
      const x = Math.random() * 512
      const w = 3 + Math.random() * 18
      const grad = g.createLinearGradient(x, 0, x + w, 0)
      grad.addColorStop(0, 'rgba(255,220,150,0)')
      grad.addColorStop(0.5, `rgba(255,230,170,${0.25 + Math.random() * 0.5})`)
      grad.addColorStop(1, 'rgba(255,220,150,0)')
      g.fillStyle = grad
      g.fillRect(x, 0, w, 64)
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping
    tex.repeat.set(6, 1)
    return tex
  }
}

// ─── Proto13/14 共用ヘルパー ───────────────────────────────

export function kanjiNum(n: number): string {
  const d = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九']
  if (n < 10) return d[n]
  if (n < 20) return `十${d[n % 10]}`
  return `${d[Math.floor(n / 10)]}十${d[n % 10]}`
}

/** 木札: 「第◯番」＋建物名（勧進帳トーン） */
export function makeKanjinTag(no: number, building: string): THREE.Mesh {
  const c = document.createElement('canvas')
  c.width = 512
  c.height = 128
  const g = c.getContext('2d')!
  g.fillStyle = '#f2ecdc'
  g.beginPath()
  g.roundRect(2, 2, 508, 124, 8)
  g.fill()
  g.strokeStyle = '#a53f2b'
  g.lineWidth = 5
  g.strokeRect(8, 8, 496, 112)
  g.textAlign = 'center'
  g.fillStyle = '#7c2f20'
  g.font = '600 40px "HGP行書体", "HG行書体", "Yu Mincho", serif'
  g.fillText(`第${kanjiNum(no)}番`, 130, 82)
  g.fillStyle = '#33291c'
  g.font = '600 44px "HGP行書体", "HG行書体", "Yu Mincho", serif'
  g.fillText(building, 340, 84)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  return new THREE.Mesh(
    new THREE.PlaneGeometry(5.9, 1.48),
    new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide }),
  )
}

/** 建造物名の3D浮遊ネーム: 行書体テキストを層状に重ねた疑似押し出し（軽量な立体文字） */
export function makeName3D(text: string, tint = 0xffd77a, backTint = 0x6b4718): THREE.Group {
  const c = document.createElement('canvas')
  c.width = 1024
  c.height = 224
  const g = c.getContext('2d')!
  g.clearRect(0, 0, 1024, 224)
  g.textAlign = 'center'
  g.textBaseline = 'middle'
  g.font = '900 150px "HGP行書体", "HG行書体", "HGS行書体", "Yu Mincho", serif'
  g.lineWidth = 14
  g.strokeStyle = 'rgba(60,30,5,0.95)'
  g.strokeText(text, 512, 118)
  g.fillStyle = `#${tint.toString(16).padStart(6, '0')}`
  g.fillText(text, 512, 118)
  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace

  const group = new THREE.Group()
  const LAYERS = 7
  for (let i = 0; i < LAYERS; i++) {
    const front = i === LAYERS - 1
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, depthWrite: false, side: THREE.DoubleSide,
      color: front ? 0xffffff : backTint,
      opacity: front ? 1 : 0.92,
    })
    const m = new THREE.Mesh(new THREE.PlaneGeometry(11, 2.4), mat)
    m.position.z = i * 0.11
    m.renderOrder = i
    group.add(m)
  }
  return group
}

export function makeWarmGlow(): THREE.Sprite {
    const c = document.createElement('canvas')
    c.width = c.height = 128
    const g = c.getContext('2d')!
    const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64)
    grad.addColorStop(0, 'rgba(255,235,180,1)')
    grad.addColorStop(0.5, 'rgba(255,200,110,0.5)')
    grad.addColorStop(1, 'rgba(255,190,90,0)')
    g.fillStyle = grad
    g.fillRect(0, 0, 128, 128)
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    return new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false,
    }))
}
